// src/client/lib/sentry.ts
// Same scrubbing pattern as @admin/lib/sentry — duplicated, not imported, to
// keep surfaces independent. Borrower flows handle OTP and KYC photos so the
// redaction list is at least as strict as admin.
import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';
import { env } from './env';

const SENSITIVE_KEY_RE = /token|auth|password|secret|otp|mfa_code|session|nrc/i;

function redactSensitive(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(redactSensitive);
  if (typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SENSITIVE_KEY_RE.test(k) ? '[REDACTED]' : redactSensitive(v);
  }
  return out;
}

export function scrubEvent(event: ErrorEvent, _hint?: EventHint): ErrorEvent {
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => {
      if (!b.data) return b;
      return { ...b, data: redactSensitive(b.data) as Record<string, unknown> };
    });
  }
  if (event.request?.headers) {
    event.request.headers = redactSensitive(event.request.headers) as Record<string, string>;
  }
  if (event.extra) {
    event.extra = redactSensitive(event.extra) as Record<string, unknown>;
  }
  return event;
}

export function initSentry(): void {
  if (!env.VITE_SENTRY_DSN) return;
  const release = import.meta.env['VITE_GIT_SHA'] as string | undefined;
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_APP_ENV,
    tracesSampleRate: env.VITE_APP_ENV === 'production' ? 0.1 : 1.0,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend: scrubEvent,
    ...(release ? { release } : {}),
  });
}

export { Sentry };
